import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, MapPin, ExternalLink, Phone, MapPinned, Trash2 } from "lucide-react";
import { CAT_LETTER, CAT_LABEL, STATUSES, STATUS_COLOR } from "@/lib/prospects";
import { useState, useEffect } from "react";
import PhotoUpload from "@/components/PhotoUpload";

// Shared prospect detail card — identical for shop and admin. Editing writes to
// the same shared record via the caller's onSave.
export default function ProspectCard({ sel, setSel, onSave, saving, onSaveToRider, savingRider, onRemoveFromRider, removingRider }) {
  const [riderPhotos, setRiderPhotos] = useState([]);
  useEffect(() => { setRiderPhotos([]); }, [sel?.id]);
  return (
    <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
      <DialogContent className="max-w-md" data-testid="prospect-card">
        {sel && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: STATUS_COLOR[sel.status] }}>{CAT_LETTER[sel.category]}</span>
                {sel.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <div className="text-muted-foreground">{CAT_LABEL[sel.category]}{sel.distance_miles != null ? ` · ${sel.distance_miles} mi from trail` : ""}</div>
              {sel.address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />{sel.address}</div>}
              {sel.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground shrink-0" /><a href={`tel:${sel.phone}`} className="text-primary">{sel.phone}</a></div>}
              {sel.website && <div className="flex items-center gap-2"><ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" /><a href={sel.website} target="_blank" rel="noreferrer" className="text-primary truncate">{sel.website}</a></div>}
              <div className="pt-2">
                <label className="text-xs font-medium text-muted-foreground">Status</label>
                <Select value={sel.status} onValueChange={(v) => setSel((s) => ({ ...s, status: v }))}>
                  <SelectTrigger className="mt-1" data-testid="prospect-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLOR[s] }} />{s}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Notes</label>
                <Textarea value={sel.notes || ""} onChange={(e) => setSel((s) => ({ ...s, notes: e.target.value }))} rows={3} placeholder="Outreach notes…" className="mt-1" data-testid="prospect-notes-input" />
              </div>
              {sel.updated_at && <div className="text-[11px] text-muted-foreground">Last updated {String(sel.updated_at).slice(0, 10)}</div>}
              {sel.category === "attraction" && (onSaveToRider || onRemoveFromRider) && (
                <div className="pt-2 border-t border-border">
                  {sel.rider_saved ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-xs text-emerald-400" data-testid="prospect-on-rider-badge"><MapPinned className="w-4 h-4" />On the Rider map</div>
                      {onRemoveFromRider && (
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => onRemoveFromRider(sel)} disabled={removingRider} data-testid="prospect-remove-rider">
                          {removingRider ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}Remove
                        </Button>
                      )}
                    </div>
                  ) : (onSaveToRider && (
                    <div className="space-y-2">
                      <PhotoUpload photos={riderPhotos} onChange={setRiderPhotos} label="Add photo" testId="prospect-card-rider-photos" />
                      <p className="text-[11px] text-muted-foreground">Optional — a photo makes the rider banner complete on approval.</p>
                      <Button variant="outline" className="w-full" onClick={() => onSaveToRider(sel, riderPhotos)} disabled={savingRider} data-testid="prospect-save-rider">
                        {savingRider ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPinned className="w-4 h-4 mr-2" />}Save to Rider map
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setSel(null)} data-testid="prospect-cancel">Cancel</Button>
              <Button onClick={onSave} disabled={saving} data-testid="prospect-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
